import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { BathroomRangeDto } from 'src/app/models/configurators-2d/common-bathroom-dtos';
import { BathroomCompositionDto } from 'src/app/models/configurators-2d/mobile/bathroom/bathroom-composition-dtos';
import { BathroomConfigurationState } from 'src/app/models/configurators-2d/mobile/bathroom/bathroom-validation.models';
import { BathroomService } from 'src/app/services/configurators-2d/applications/bathroom.service';
import { BathroomConfigurationService } from 'src/app/services/configurators-2d/mobile/bathroom/bathroom-configuration.service';
import { CustomerService } from 'src/app/services/customer.service';
import { isGuidValue } from 'src/app/services/helpers/utils';
import { MODAL, ModalService } from 'src/app/services/modal.service';
import { BathroomConfigurationData } from 'src/app/stores/tunnel/model/configuration-data/bathroom-configuration-data';
import { TunnelDataStore } from 'src/app/stores/tunnel/tunnel-data.store';
import { TunnelNavigationStore } from 'src/app/stores/tunnel/tunnel-navigation.store';
import {
  DesktopConfiguratorAction,
  DesktopConfiguratorOutlet,
  DesktopConfiguratorStep,
} from '../../common/desktop-configurator-template/desktop-configurator-template.models';
import { BathroomRangeSelectorComponent } from 'src/app/pages/configurators-2d/mobile/bathroom/bathroom-range-selector/bathroom-range-selector.component';
import { CompositionComponent } from 'src/app/pages/configurators-2d/mobile/bathroom/composition/composition.component';
import { BathroomConfigurationComponent } from 'src/app/pages/configurators-2d/mobile/bathroom/bathroom-configuration/bathroom-configuration.component';
import { BathroomConfigurationPreviewComponent } from '../bathroom-configuration-preview/bathroom-configuration-preview.component';
import { BathroomTunnelConceptionSummaryComponent } from './bathroom-tunnel-conception-summary.component';

type BathroomConceptionStepId = 'range' | 'composition' | 'configuration' | 'summary';

@Component({
  selector: 'ng-casto-bathroom-tunnel-conception',
  templateUrl: './bathroom-tunnel-conception.component.html',
  styleUrls: ['./bathroom-tunnel-conception.component.scss'],
})
export class BathroomTunnelConceptionComponent implements OnInit, OnDestroy {
  private readonly bathroomService = inject(BathroomService);
  private readonly configurationService = inject(BathroomConfigurationService);
  private readonly customerService = inject(CustomerService);
  private readonly modalService = inject(ModalService);
  private readonly route = inject(ActivatedRoute);
  private readonly tunnelDataStore = inject(TunnelDataStore);
  private readonly tunnelNavigationStore = inject(TunnelNavigationStore);
  private readonly subscriptions: Subscription[] = [];

  protected currentStepId: BathroomConceptionStepId = 'range';
  protected selectedRange: BathroomRangeDto | null = null;
  protected selectedComposition: BathroomCompositionDto | null = null;
  protected configuration: BathroomConfigurationState | null = null;
  protected finalizing = false;
  protected error: string | null = null;
  protected panelCollapsed = false;
  protected steps: DesktopConfiguratorStep[] = [];
  protected preview: DesktopConfiguratorOutlet | null = null;
  protected actions: DesktopConfiguratorAction[] = [];
  private hideRangeStep = false;
  private shouldUpdateThumbnail = false;
  private stepNavigationVersion = 0;
  private unregisterBeforeNavigate: (() => void) | null = null;

  constructor() {
    this.subscriptions.push(
      this.configurationService.configuration$.subscribe(configuration => {
        this.configuration = configuration;
      })
    );
  }

  async ngOnInit(): Promise<void> {
    this.unregisterBeforeNavigate = this.tunnelNavigationStore.registerBeforeNavigate((nextIndex, currentIndex) =>
      this.beforeTunnelNavigate(nextIndex, currentIndex)
    );
    await this.loadRouteConfiguration();
    await this.initializeRangeStep();
  }

  ngOnDestroy(): void {
    this.unregisterBeforeNavigate?.();
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  protected async onStepSelected(stepId: string): Promise<void> {
    const nextStepId = stepId as BathroomConceptionStepId;
    const navigationVersion = ++this.stepNavigationVersion;

    if (!(await this.canLeaveCurrentStep(nextStepId))) return;
    if (navigationVersion !== this.stepNavigationVersion) return;

    this.goToStep(nextStepId);
  }

  protected onPanelCollapsedChange(collapsed: boolean): void {
    this.panelCollapsed = collapsed;
  }

  private onRangeSelected(range: BathroomRangeDto): void {
    this.selectedRange = range;
    this.selectedComposition = null;
    this.configuration = null;
    this.goToStep('composition');
  }

  private onCompositionSelected(composition: BathroomCompositionDto): void {
    this.selectedComposition = composition;
    this.shouldUpdateThumbnail = false;
    this.goToStep('configuration');
  }

  private onConfigurationValidated(configuration: BathroomConfigurationState): void {
    this.configuration = configuration;
    this.shouldUpdateThumbnail = true;
    this.goToStep('summary');
  }

  private goToStep(stepId: BathroomConceptionStepId): void {
    if (!this.canOpenStep(stepId)) return;
    this.currentStepId = stepId;
    if (stepId === 'configuration') this.shouldUpdateThumbnail = true;
    if (stepId !== 'configuration') this.panelCollapsed = false;
    this.refreshTemplate();
  }

  private canOpenStep(stepId: BathroomConceptionStepId): boolean {
    if (stepId === 'range' && this.hideRangeStep) return false;
    if (stepId === 'range') return true;
    if (stepId === 'composition') return !!this.selectedRange;
    if (stepId === 'configuration') return !!this.selectedComposition;
    if (stepId === 'summary') return !!this.configuration?.composition;
    return false;
  }

  private async canLeaveCurrentStep(nextStepId: BathroomConceptionStepId): Promise<boolean> {
    if (this.currentStepId !== 'configuration' || nextStepId !== 'summary') return true;
    if (!this.configuration?.composition) return true;

    const capturedFromVisiblePreview = await this.configurationService.captureVisiblePreviewImage();
    this.shouldUpdateThumbnail = capturedFromVisiblePreview || this.shouldUpdateThumbnail;

    return true;
  }

  private isStepCompleted(stepId: BathroomConceptionStepId): boolean {
    if (stepId === 'range') return !!this.selectedRange;
    if (stepId === 'composition') return !!this.selectedComposition;
    if (stepId === 'configuration') return !!this.configuration?.composition;
    return false;
  }

  private refreshTemplate(): void {
    this.steps = this.buildSteps();
    this.preview = this.buildPreview();
    this.actions = this.buildActions();
  }

  private buildSteps(): DesktopConfiguratorStep[] {
    const rangeStep: DesktopConfiguratorStep = {
      id: 'range',
      label: 'Choix de la gamme',
      component: BathroomRangeSelectorComponent,
      inputs: { embedded: true, layoutMode: 'desktop', autoSelectFirstRange: false },
      outputs: { rangeSelected: value => this.onRangeSelected(value as BathroomRangeDto) },
      completed: () => this.isStepCompleted('range'),
    };

    const steps: DesktopConfiguratorStep[] = [
      {
        id: 'composition',
        label: 'Choix de la composition',
        component: CompositionComponent,
        inputs: { embedded: true, layoutMode: 'desktop', rangeIdInput: this.selectedRange?.id ?? null },
        outputs: {
          compositionSelected: value => this.onCompositionSelected(value as BathroomCompositionDto),
          backRequested: () => this.goToStep('range'),
        },
        disabled: () => !this.canOpenStep('composition'),
        completed: () => this.isStepCompleted('composition'),
      },
      {
        id: 'configuration',
        label: 'Configuration & finitions',
        component: BathroomConfigurationComponent,
        inputs: {
          embedded: true,
          layoutMode: 'desktop',
          compositionIdInput: this.selectedComposition?.id ?? null,
          hideRender: true,
        },
        outputs: {
          configurationValidated: value => this.onConfigurationValidated(value as BathroomConfigurationState),
          backRequested: () => this.goToStep('composition'),
        },
        disabled: () => !this.canOpenStep('configuration'),
        completed: () => this.isStepCompleted('configuration'),
      },
      {
        id: 'summary',
        label: 'Prix & récapitulatif',
        component: BathroomTunnelConceptionSummaryComponent,
        inputs: {
          configuration: this.configuration,
          rangeName: this.getRangeName(),
          compositionName: this.getCompositionName(),
          imageSrc: this.getSummaryImage(),
          productsCount: this.getProductsCount(),
          error: this.error,
        },
        disabled: () => !this.canOpenStep('summary'),
      },
    ];

    return this.hideRangeStep ? steps : [rangeStep, ...steps];
  }

  private async initializeRangeStep(): Promise<void> {
    try {
      const ranges = await this.bathroomService.getAllRange();
      const existingConfiguration = this.configurationService.getCurrentConfiguration();
      const existingComposition = existingConfiguration.composition;
      this.hideRangeStep = ranges.length === 1;

      if (existingComposition) {
        const rangeId = existingComposition.compositionConfiguration?.rangeId;
        this.selectedRange =
          ranges.find(range => range.id?.toLowerCase() === rangeId?.toLowerCase()) ??
          (this.hideRangeStep ? ranges[0] : null);
        this.selectedComposition = {
          id: existingComposition.id,
          name: existingComposition.name,
          thumbnailUrl: existingComposition.thumbnailUrl,
          compositionConfigurationId: existingComposition.compositionConfigurationId,
        };
        this.configuration = existingConfiguration;
        this.currentStepId = 'summary';
      } else if (this.hideRangeStep) {
        this.selectedRange = ranges[0];
        this.currentStepId = 'composition';
      }
    } catch (error) {
      console.error('Erreur lors du chargement des gammes salle de bain', error);
      this.hideRangeStep = false;
      this.currentStepId = 'range';
    } finally {
      this.refreshTemplate();
    }
  }

  private buildPreview(): DesktopConfiguratorOutlet | null {
    if (this.currentStepId !== 'configuration') return null;
    return { component: BathroomConfigurationPreviewComponent };
  }

  private buildActions(): DesktopConfiguratorAction[] {
    return [
      {
        id: 'continue',
        label: 'Continuer',
        icon: 'arrow-right',
        primary: true,
        disabled: () => this.currentStepId !== 'summary' || !this.configuration?.composition,
        loading: () => this.finalizing,
        action: () => this.continueToProducts(),
      },
    ];
  }

  private getSummaryImage(): string {
    return this.configurationService.getValidSceneImage() ?? 'assets/img/bathroom/kentia_1.png';
  }

  private getRangeName(): string {
    return this.selectedRange?.name ?? this.configuration?.composition?.compositionConfiguration?.rangeId ?? '-';
  }

  private getCompositionName(): string {
    return this.configuration?.composition?.name ?? this.selectedComposition?.name ?? '-';
  }

  private getProductsCount(): number {
    const rows = this.configuration?.composition?.compositionConfiguration?.rows ?? [];
    return rows.reduce((count, row) => {
      const rowProducts = (row.coverProduct ? 1 : 0) + (row.basinProduct ? 1 : 0) + (row.columns?.length ?? 0);
      return count + rowProducts;
    }, 0);
  }

  private async continueToProducts(): Promise<void> {
    if (this.finalizing) return;

    await this.selectCustomerIfNeeded(async () => {
      if (await this.saveConfiguration()) {
        this.tunnelNavigationStore.nextStep();
      }
    });
  }

  private selectCustomerIfNeeded(callback: () => Promise<void>): Promise<void> {
    if (this.customerService.userDatas) {
      return callback();
    }

    return new Promise<void>((resolve, reject) => {
      const onSuccess = async () => {
        this.modalService.close(true);
        try {
          await callback();
          resolve();
        } catch (error) {
          reject(error);
        }
      };

      const onClose = () => {
        if (this.modalService.returnValue !== true) {
          resolve();
        }
      };

      this.modalService.display(
        MODAL.SEARCH_CUSTOMER,
        { configContext: CONFIG_CONTEXT.BATHROOM_V2, onCustomerSelect: onSuccess },
        false,
        onClose
      );
    });
  }

  private async beforeTunnelNavigate(nextIndex: number, currentIndex: number): Promise<boolean> {
    if (currentIndex !== 0 || nextIndex <= currentIndex) return true;
    if (!this.configuration?.composition) return true;
    if (!this.shouldUpdateThumbnail && this.currentStepId !== 'configuration') return true;

    return await this.saveConfiguration();
  }

  private async saveConfiguration(): Promise<boolean> {
    this.finalizing = true;
    this.error = null;
    this.refreshTemplate();
    try {
      if (this.currentStepId === 'configuration') {
        const capturedFromVisiblePreview = await this.configurationService.captureVisiblePreviewImage();
        this.shouldUpdateThumbnail = !!capturedFromVisiblePreview || this.shouldUpdateThumbnail;
      }

      const isUpdate = !!this.getCurrentBathroomConfigurationData()?.configurationId;
      const shouldSendThumbnail = !isUpdate || this.shouldUpdateThumbnail;
      if (isUpdate && !shouldSendThumbnail) {
        await this.refreshPricingForCurrentConfiguration();
        return true;
      }

      if ((!isUpdate || this.shouldUpdateThumbnail) && !this.configurationService.getValidSceneImage()) {
        await this.configurationService.captureVisiblePreviewImage();
      }

      const products = await this.configurationService.buildConfigurationProducts();
      const thumbnailData = this.configurationService.getValidSceneImage();
      if (!isUpdate && !thumbnailData) {
        throw new Error('La thumbnail de la configuration salle de bain est requise à la création.');
      }

      const currentConfiguration = this.configurationService.getCurrentConfiguration();
      const configurationData: BathroomConfigurationData = {
        projectTitle: 'Ma salle de bain',
        products,
        compositionId: currentConfiguration.composition?.id,
        compositionName: currentConfiguration.composition?.name,
        compositionThumbnailUrl: currentConfiguration.composition?.thumbnailUrl,
        compositionConfiguration: this.configurationService.getPersistableCompositionConfiguration(),
      };
      if (shouldSendThumbnail && thumbnailData) configurationData.thumbnail = { data: thumbnailData };
      await this.bathroomService.createBathroom(configurationData, { updateThumbnail: this.shouldUpdateThumbnail });

      const configuration = this.getCurrentBathroomConfigurationData();

      if (configuration?.configurationId && configuration.products?.length > 0) {
        // Chargement : on laisse l'API répondre avec l'âge d'habitation persisté du projet.
        await this.bathroomService.computeInstallation({ usePersistedHouseAge: true });
        await this.bathroomService.loadBathroomPricing(configuration.configurationId);
      }

      return true;
    } catch (error) {
      console.error('Erreur lors de la création du tunnel salle de bain 2D', error);
      this.error = 'Impossible de transformer la configuration en liste de produits.';
      this.refreshTemplate();
      return false;
    } finally {
      this.finalizing = false;
      this.refreshTemplate();
    }
  }

  private async refreshPricingForCurrentConfiguration(): Promise<void> {
    const configuration = this.getCurrentBathroomConfigurationData();

    if (configuration?.configurationId && configuration.products?.length > 0) {
      // Chargement : on laisse l'API répondre avec l'âge d'habitation persisté du projet.
      await this.bathroomService.computeInstallation({ usePersistedHouseAge: true });
      await this.bathroomService.loadBathroomPricing(configuration.configurationId);
    }
  }

  private async loadRouteConfiguration(): Promise<void> {
    const configurationId = this.getRouteConfigurationId();
    if (!configurationId) return;

    try {
      const configuration = await this.bathroomService.loadBathroomConfigurationFromId(configurationId);
      await this.configurationService.restoreConfigurationFromTunnel(configuration);
    } catch (error) {
      console.error('Erreur lors du rechargement de la configuration salle de bain', error);
      this.error = 'Impossible de recharger la configuration enregistrée.';
    }
  }

  private getRouteConfigurationId(): string | null {
    const routeValue = this.route.snapshot.paramMap.get('startIndex');
    return isGuidValue(routeValue) ? routeValue : null;
  }

  private getCurrentBathroomConfigurationData(): BathroomConfigurationData {
    return this.tunnelDataStore
      .getCurrentStateValue()
      .configurationData.get(CONFIG_CONTEXT.BATHROOM_V2) as BathroomConfigurationData;
  }
}
